import type { ChartKind } from "../types";

/**
 * Remembers, for the browser session, which chart kind a user switched to.
 *
 * Scoped to the conversation rather than the user: a preference is a reaction to the shape of the
 * data being discussed, and carrying "always bar" into an unrelated conversation about a six-month
 * trend would override a recommendation that was right. Within one conversation the datasets are
 * related enough that the preference usually still fits.
 *
 * Deliberately module state and not the Zustand store: nothing else needs to read it, it must not
 * survive a reload (a stale preference against fresh data is a worse default than the platform's own
 * choice), and it must never reach the backend. It is a UI convenience, not tenant data.
 */
const preferences = new Map<string, ChartKind>();

/** Conversations kept before the oldest is evicted — a bound, not a policy. */
const MAX_CONVERSATIONS = 50;

/** The kind this conversation last switched to, if any. */
export function getPreferredKind(conversationId: string | null): ChartKind | undefined {
  return conversationId ? preferences.get(conversationId) : undefined;
}

/**
 * Records a switch.
 *
 * Only ever called from an explicit click. A kind the user never chose — the recommendation itself —
 * is not recorded, so the preference reflects a decision rather than a default they passed through.
 */
export function rememberPreferredKind(conversationId: string | null, kind: ChartKind): void {
  if (!conversationId) return;
  // Re-insert so the most recently used conversation is last in iteration order.
  preferences.delete(conversationId);
  preferences.set(conversationId, kind);
  if (preferences.size > MAX_CONVERSATIONS) {
    const oldest = preferences.keys().next();
    if (!oldest.done) preferences.delete(oldest.value);
  }
}

/** Drops a conversation's preference. Used when a conversation is deleted. */
export function forgetPreferredKind(conversationId: string | null): void {
  if (conversationId) preferences.delete(conversationId);
}
